// Reports (#/reports): closed-event export for billing and reporting until the ERP link (Phase 6).
// The download is fetched with the auth headers and saved through a blob link.

import { useState } from 'react';
import { api, downloadAuthed } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { Card, InlineError, Seg } from '../components/ui.tsx';
import { useAction } from '../hooks.ts';

function dayStr(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Local calendar day → ISO instant at local midnight (end is exclusive: the next midnight). */
function dayStart(day: string): string {
  return new Date(`${day}T00:00:00`).toISOString();
}
function dayEndExclusive(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString();
}

export function ReportsScreen() {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 6 * 86_400_000);
  const [from, setFrom] = useState(dayStr(weekAgo));
  const [to, setTo] = useState(dayStr(today));
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [done, setDone] = useState<string | null>(null);
  const dl = useAction(async (url: string, name: string) => {
    await downloadAuthed(url, name);
    return true;
  });
  const bad = !from || !to || from > to;

  const run = async () => {
    setDone(null);
    const url = api.closedEventsExportUrl({ from: dayStart(from), to: dayEndExclusive(to), format });
    const name = `closed-events_${from}_${to}.${format}`;
    const r = await dl.run(url, name);
    if (r) setDone(name);
  };

  return (
    <div className="page">
      <div className="page-h">
        <div>
          <h2>Reports</h2>
          <p>Exports of closed events for billing and customer reporting. The ERP link replaces this in Phase 6.</p>
        </div>
      </div>
      <Card
        title={
          <>
            <Icon name="download" />
            Closed events export
          </>
        }
      >
        <div className="filters">
          <label className="field inline">
            <span className="lbl">From (your time)</span>
            <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field inline">
            <span className="lbl">To, inclusive</span>
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </label>
          <div className="field">
            <span className="lbl">Format</span>
            <Seg
              label="Format"
              value={format}
              onChange={setFormat}
              options={[
                { value: 'csv', label: 'CSV' },
                { value: 'json', label: 'JSON' },
              ]}
            />
          </div>
          <div className="field end">
            <button type="button" className="btn pri" onClick={() => void run()} disabled={bad || dl.busy}>
              <Icon name="download" />
              {dl.busy ? 'Preparing…' : 'Download'}
            </button>
          </div>
        </div>
        {bad && <span className="inline-err">Pick a start date on or before the end date.</span>}
        <InlineError error={dl.error} />
        {done && !dl.error && !dl.busy && <span className="ok-t">Downloaded {done}.</span>}
        <div className="note">
          Includes the event.closed fields: event number, site, times, disposition code, billable flag, customer report status, and who handled it. Dates are your local days, sent as UTC.
        </div>
      </Card>
    </div>
  );
}
